import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { useAuth } from './hooks/useAuth';
import AdminLayout from './components/layout/AdminLayout';
import './assets/styles/global.css';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/dashboard'));
const UserList = lazy(() => import('./pages/users'));
const UserDetail = lazy(() => import('./pages/users/UserDetail'));
const AvatarList = lazy(() => import('./pages/avatars'));
const ContentMoments = lazy(() => import('./pages/content/Moments'));
const ContentMessages = lazy(() => import('./pages/content/Messages'));
const ContentReports = lazy(() => import('./pages/content/Reports'));
const GroupList = lazy(() => import('./pages/groups'));
const SubscriptionPlans = lazy(() => import('./pages/subscriptions/Plans'));
const SubscriptionOrders = lazy(() => import('./pages/subscriptions/Orders'));
const SettingsGeneral = lazy(() => import('./pages/settings/General'));
const LogsOperation = lazy(() => import('./pages/logs/Operation'));
const LogsLogin = lazy(() => import('./pages/logs/Login'));
const LogsError = lazy(() => import('./pages/logs/Error'));
const AIScenariosPage = lazy(() => import('./pages/ai/ScenariosPage'));
const AIPromptsPage = lazy(() => import('./pages/ai/PromptsPage'));
const AIModelsPage = lazy(() => import('./pages/ai/ModelsPage'));
const AIRoutesPage = lazy(() => import('./pages/ai/RoutesPage'));
const AISkillsPage = lazy(() => import('./pages/ai/SkillsPage'));
const AIPoliciesPage = lazy(() => import('./pages/ai/PoliciesPage'));
const AIContextBundlesPage = lazy(() => import('./pages/ai/ContextBundlesPage'));
const AIExperimentsPage = lazy(() => import('./pages/ai/ExperimentsPage'));
const AITracesPage = lazy(() => import('./pages/ai/TracesPage'));
const AIShadowAuditsPage = lazy(() => import('./pages/ai/ShadowAuditsPage'));
const AIPlaygroundPage = lazy(() => import('./pages/ai/PlaygroundPage'));
const AIPsychConfigPage = lazy(() => import('./pages/ai/PsychConfigPage'));
const AIPsychOpsPage = lazy(() => import('./pages/ai/PsychOpsPage'));
const AITrustPage = lazy(() => import('./pages/ai/TrustPage'));

const PageLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? children : <Navigate to="/" replace />;
};

function App() {
  return (
    <ConfigProvider locale={zhCN} theme={{ token: { colorPrimary: '#1890ff' } }}>
      <BrowserRouter>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/" element={
              <PrivateRoute>
                <AdminLayout />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="users" element={<UserList />} />
              <Route path="users/:id" element={<UserDetail />} />
              <Route path="avatars" element={<AvatarList />} />
              <Route path="content/moments" element={<ContentMoments />} />
              <Route path="content/messages" element={<ContentMessages />} />
              <Route path="content/reports" element={<ContentReports />} />
              <Route path="groups" element={<GroupList />} />
              <Route path="subscriptions/plans" element={<SubscriptionPlans />} />
              <Route path="subscriptions/orders" element={<SubscriptionOrders />} />
              <Route path="settings/general" element={<SettingsGeneral />} />
              <Route path="logs/operation" element={<LogsOperation />} />
              <Route path="logs/login" element={<LogsLogin />} />
              <Route path="logs/error" element={<LogsError />} />
              <Route path="ai/scenarios" element={<AIScenariosPage />} />
              <Route path="ai/prompts" element={<AIPromptsPage />} />
              <Route path="ai/models" element={<AIModelsPage />} />
              <Route path="ai/routes" element={<AIRoutesPage />} />
              <Route path="ai/skills" element={<AISkillsPage />} />
              <Route path="ai/policies" element={<AIPoliciesPage />} />
              <Route path="ai/context-bundles" element={<AIContextBundlesPage />} />
              <Route path="ai/experiments" element={<AIExperimentsPage />} />
              <Route path="ai/traces" element={<AITracesPage />} />
              <Route path="ai/shadow-audits" element={<AIShadowAuditsPage />} />
              <Route path="ai/playground" element={<AIPlaygroundPage />} />
              <Route path="ai/psych-config" element={<AIPsychConfigPage />} />
              <Route path="ai/psych-ops" element={<AIPsychOpsPage />} />
              <Route path="ai/trust" element={<AITrustPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
